import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/AuthProvider';
import { Building2, Users, Lock, ArrowRight } from 'lucide-react';

interface Organization {
  id: string;
  name: string;
  permissions: string[]; // e.g., ['read', 'write', 'admin']
}

interface OrganizationSelectorProps {
  onSelectOrganization: (organizationId: string) => void;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ 
  onSelectOrganization 
}) => {
  // UPDATED: Get permissions from AuthProvider instead of loading them separately
  const { currentUser, loading: authLoading, fullUserPermissions } = useAuth();
  const [error, setError] = useState<string>('');

  // UPDATED: Extract organization permissions from AuthProvider data
  const getOrganizationPermissions = (): Organization[] => {
    if (!fullUserPermissions?.orgPermissions) return [];

    const orgPermissions: { [key: string]: Set<string> } = {};
    
    // Parse orgPermissions array for organization-specific permissions
    fullUserPermissions.orgPermissions.forEach(permission => {
      if (permission.includes('_')) {
        const parts = permission.split('_');
        if (parts.length >= 2) {
          const orgId = parts[0];
          const permType = parts[1]; // 'read', 'write', 'admin', 'api'
          
          if (!orgPermissions[orgId]) {
            orgPermissions[orgId] = new Set();
          }
          orgPermissions[orgId].add(permType);
        }
      }
    });

    return Object.entries(orgPermissions).map(([id, perms]) => ({
      id,
      name: formatOrganizationName(id),
      permissions: Array.from(perms)
    }));
  };

  // Helper function to format organization names nicely
  const formatOrganizationName = (id: string): string => {
    // Convert camelCase or UPPERCASE to proper names
    return id
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add spaces before capitals
      .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getPermissionBadges = (permissions: string[]) => {
    const badgeColors: { [key: string]: string } = {
      admin: 'bg-red-100 text-red-800',
      write: 'bg-blue-100 text-blue-800',
      read: 'bg-green-100 text-green-800',
      api: 'bg-purple-100 text-purple-800'
    };

    return permissions.map(perm => (
      <span 
        key={perm} 
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeColors[perm] || 'bg-gray-100 text-gray-800'}`}
      >
        {perm}
      </span>
    ));
  };

  const getHighestPermission = (permissions: string[]): string => {
    if (permissions.includes('admin')) return 'admin';
    if (permissions.includes('write')) return 'write';
    if (permissions.includes('api')) return 'api';
    return 'read';
  };

  const getBorderColor = (permission: string): string => {
    switch (permission) {
      case 'admin': return 'border-red-200 hover:border-red-300';
      case 'write': return 'border-blue-200 hover:border-blue-300';
      case 'api': return 'border-purple-200 hover:border-purple-300';
      default: return 'border-green-200 hover:border-green-300';
    }
  };

  const organizations = getOrganizationPermissions();

  // If user has access to only one organization, auto-select it
  useEffect(() => {
    if (organizations.length === 1 && !authLoading && currentUser) {
      onSelectOrganization(organizations[0].id);
    }
  }, [organizations, onSelectOrganization, authLoading, currentUser]);

  // If only one organization, don't render the selector
  if (organizations.length <= 1 && !authLoading) {
    return null;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Organizations...</h2>
          <p className="text-gray-600">Please wait while we load your organization data</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <Building2 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Select Organization</h1>
          <p className="text-lg text-gray-600">Choose an organization to manage its collections and data</p>
        </div>
        
        {organizations.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
            {organizations.map(org => {
              const highestPermission = getHighestPermission(org.permissions);
              return (
                <div
                  key={org.id}
                  className={`bg-white rounded-lg border-2 ${getBorderColor(highestPermission)} p-6 cursor-pointer transition-all duration-200 hover:shadow-lg group`}
                  onClick={() => onSelectOrganization(org.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {org.name}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">ID: {org.id}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <Lock className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm font-medium text-gray-700">Permissions</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {getPermissionBadges(org.permissions)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <button 
                      type="button"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectOrganization(org.id);
                      }}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Manage Organization
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center max-w-md mx-auto">
            <div className="bg-white rounded-lg border border-gray-200 p-8">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Organizations Available</h3>
              <p className="text-gray-600 mb-6">You don't have access to any organizations. Please contact an administrator to get access.</p>
              <button 
                onClick={() => window.location.reload()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        )}

        {/* User info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            Logged in as: <span className="font-medium">{currentUser?.email}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelector;